import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

import { Typography } from '../../../../../components/display/Typography';
import { ButtonIconBackground } from '../../../../../components/inputs/buttons/ButtonIconBackground';
import { SpecialTag } from '../../../../../components/display/SpecialTag';
import { variant } from '../../../../../components/display/SpecialTag/types';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag';

// Primitivos canónicos de los reports full-page (HistoricalActionsReport
// → ValidationsTable). NO usar `summaryTables/SummaryTablesStyles` aquí:
// ese set viene con `Thead { background: greyDark }` + `Tbody { background:
// greyVerySoft }` + `TableContainer { height: 19.2rem; overflow: auto }`,
// diseñado para tablas resumen embebidas DENTRO de formularios con scroll
// interno. Para un report full-page queremos headers blancos con underline
// negro + body blanco, idéntico a HistoricalActions/HistoricalAccidents.
import {
  Table,
  Tbody,
  Th,
  Td as CanonicalTd,
  TextHeader,
} from '../../../../../components/display/tables/components/MainStyledComponents';

import { formatHHMM, formatEstimatedHours, formatDateTime } from '../utils';
import { OperatorRow } from '../types';
import { TableContainerStyled } from '../styles';
import { compressSessionStorage } from '../../../../../utils/sessionStorage';
import {
  formActionNaming,
  formActionValidationNaming,
} from '../../../../../components/forms/ActionForms/constants';
import { externalCompaniesNaming } from '../../../../../components/display/tables/formTables/ExternalCompaniesSelectionTable/constants';
import { actionTabsUnlockAll } from '../../../../../state/actions/ui/actionTabs';

export type { OperatorRow };

interface Props {
  rows: OperatorRow[];
  teamNameById: Record<string, string>;
  isLoading?: boolean;
  /**
   * Totales globales por naturaleza (sobre TODAS las filas, no la página).
   * Se pintan en el pie de la tabla como subtotales SEPARADOS (reales
   * internas / estimadas externas). Nunca un total mixto.
   */
  summary?: {
    internalMinutes: number;
    externalMinutes: number;
    internalCount: number;
    externalCount: number;
  };
}

const KIND_EXTERNAL = 'external-estimated';

// Tabla canónica + un único ajuste: la PRIMERA columna (Operario/Empresa) se
// indenta a `spacing.l` para alinearse con el texto de las cabeceras de
// sección (que ya van a `spacing.l`). Sin esto, al quitar la columna "Tipo"
// los nombres quedaban pegados al borde izquierdo (0.8rem canónico) y
// descuadrados respecto a las secciones. El fondo de cada fila/sección sigue
// abarcando todo el ancho (el padding es interno a la celda).
const StyledTable = styled(Table)`
  th:first-child,
  td:first-child {
    padding-left: ${({ theme }) => theme.spacing.l};
  }
`;

// `Td` canónico es genérico tipado con `cellProps` (column, isFigure, etc).
// Mi report no usa esas props (sólo Td plano), así que castea sin warning.
const Td = CanonicalTd as unknown as React.FC<
  React.TdHTMLAttributes<HTMLTableCellElement> & { colSpan?: number }
>;

// El Tr canónico de MainStyledComponents requiere `checked: boolean` por
// trProps (diseñado para tablas seleccionables). Mi report no selecciona
// rows, así que defino el Tr local — mismo `cursor: pointer` + hover sutil.
const ClickableTr = styled.tr`
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.colors.greyVerySoft};
  }
`;

// Sub-row de OTs (expandido) con tono ligeramente diferenciado para
// jerarquía, en línea con cómo HistoricalActions distingue rows seleccionadas
// (ver ValidationsTable/styles.ts TrStyled).
const SubRow = styled.tr`
  background: ${({ theme }) => theme.colors.greyVerySoft};
`;

// Sin padding horizontal → la sub-tabla expandida usa TODO el ancho de la fila
// (antes el spacing.l lateral la indentaba y no se expandía del todo).
const SubCell = styled.td`
  padding: ${({ theme }) => theme.spacing.s} 0;
`;

const SubTable = styled.table`
  width: 100%;
  text-align: left;
  font-size: ${({ theme }) => theme.fontSize.S};
  border-collapse: collapse;
`;

const SubTh = styled.th`
  padding: ${({ theme }) => theme.spacing.xxs}
    ${({ theme }) => theme.spacing.l};
  font-weight: ${({ theme }) => theme.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.greyDark};
  border-bottom: 0.1rem solid ${({ theme }) => theme.colors.greyLight};
`;

const SubTd = styled.td`
  padding: ${({ theme }) => theme.spacing.xxs}
    ${({ theme }) => theme.spacing.l};
  border-bottom: 0.1rem solid ${({ theme }) => theme.colors.greyLight};
`;

// Celda de la acción "ir a la actuación": alineada a la derecha y ancho
// mínimo (solo el icono), para no robar espacio a las columnas de datos.
const ActionSubTd = styled(SubTd)`
  text-align: right;
  width: 1%;
  white-space: nowrap;
`;

// Celda de descripción del trabajo: puede ser larga → ancho máximo con
// ellipsis (el texto completo queda en el `title` al pasar el ratón).
const SubDescTd = styled(SubTd)`
  max-width: 28rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Celda de fecha+hora: nowrap para que "dd/MM/yyyy HH:mm" NO parta la hora a
// una segunda línea (la hora queda a la misma altura que la fecha).
const DateTimeSubTd = styled(SubTd)`
  white-space: nowrap;
`;

const ChipsRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xxs};
  flex-wrap: wrap;
`;

// `Chip` styled reusado: mismo pill que para los tipos de hora. Para la
// categoría del operario (interno/externo) cambio sólo el color del
// borde y del texto — manteniendo padding/radius/font-size idénticos
// para coherencia visual interna del componente (commit `d7d987c` quitó
// la columna por falta de paridad canónica con SpecialTag; Pass 7 docs
// confirma que la categoría SÍ tiene sentido — `internal`/`external-
// estimated` viene del schema GraphQL slice 1 + 1.5).
const Chip = styled.span<{ $variant?: 'default' | 'external' }>`
  /* inline-block + nowrap: siendo un span inline, una etiqueta larga
     ("Recorrido de vigilancia") partía en dos líneas y el borde se dibujaba
     alrededor de CADA fragmento (dos pills rotos, solapando la fila). Además
     el min-content de la columna era sólo la palabra más larga, así que la
     tabla ni le reservaba ancho. Con nowrap el pill es una caja única y la
     columna pide el ancho que necesita (el contenedor ya tiene overflow-x). */
  display: inline-block;
  white-space: nowrap;
  background: ${({ theme }) => theme.colors.white};
  border: 0.1rem solid ${({ theme }) => theme.colors.greyLight};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: ${({ theme }) => theme.spacing.xxxs}
    ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.fontSize.XXS};
  font-variant-numeric: tabular-nums;

  ${({ $variant, theme }) =>
    $variant === 'external' &&
    css`
      border-color: ${theme.colors.purple};
      color: ${theme.colors.purple};
    `}
`;

const ExpandIcon = styled.span<{ $expanded: boolean }>`
  display: inline-block;
  margin-left: ${({ theme }) => theme.spacing.xs};
  transition: transform 0.15s ease;
  transform: ${({ $expanded }) => ($expanded ? 'rotate(90deg)' : 'rotate(0)')};
  user-select: none;
`;

// Celda numérica genérica: alineada a la derecha con cifras tabulares,
// igual que las columnas "Operarios" y "Total horas". Mantener el header
// (`NumTh`) y la celda con la MISMA alineación evita el efecto de
// desalineación que reportó el usuario (header izquierda + cifra derecha).
const NumCell = styled(Td)`
  font-variant-numeric: tabular-nums;
  text-align: right;
`;

// Header numérico alineado a la derecha para que el texto del título quede
// justo encima de su cifra (que también va a la derecha).
const NumTh = styled(Th)`
  text-align: right;
`;

// Empty inline en el tbody — texto en italic gris suave, mínimo visual
// (las tablas canónicas no muestran nada: tienen "0 elementos" en
// la pagination row, que yo no tengo). Mantengo el data-testid para tests.
const EmptyCell = styled.td`
  padding: ${({ theme }) => theme.spacing.l};
  text-align: center;
  color: ${({ theme }) => theme.colors.greyDark};
  font-size: ${({ theme }) => theme.fontSize.S};
  font-style: italic;
`;

// Cabecera de SECCIÓN (● Personal interno / ◆ Empresas externas). Agrupar
// por naturaleza con su título evita que se lea un total externo (estimado
// combinado) como si fuera real por persona — el dolor #1 del informe.
const SectionHeaderRow = styled.tr`
  background: ${({ theme }) => theme.colors.greyVerySoft};
`;

const SectionHeaderCell = styled.td`
  padding: ${({ theme }) => theme.spacing.s} ${({ theme }) => theme.spacing.l};
  font-weight: ${({ theme }) => theme.fontWeight.semiBold};
  font-size: ${({ theme }) => theme.fontSize.S};
  border-bottom: 0.1rem solid ${({ theme }) => theme.colors.greyLight};
`;

// Hint secundario dentro de la cabecera de sección (p.ej. la fórmula de la
// estimación externa). Mismo renglón que el título, en tono atenuado y más
// pequeño — integrado, no un banner aparte que rompe el diseño.
const SectionHint = styled.span`
  font-weight: normal;
  font-size: ${({ theme }) => theme.fontSize.XXS};
  color: ${({ theme }) => theme.colors.greyDark};
`;

// Texto del total ESTIMADO (externo) en las FILAS: atenuado (color
// secundario) → "lo menos cierto, menos prominente".
const EstimatedText = styled.span`
  color: ${({ theme }) => theme.colors.greyDark};
`;

// Fila de subtotal por sección. Mismo lenguaje visual que la cabecera de
// sección (fondo greyVerySoft, bordes greyLight heredados de Td) — sin líneas
// negras añadidas. Va justo debajo de las filas de su naturaleza.
const SubtotalRow = styled.tr`
  background: ${({ theme }) => theme.colors.greyVerySoft};
`;

const SubtotalLabelCell = styled(Td)`
  text-align: right;
`;

export function OperatorsHoursTable({
  rows,
  teamNameById,
  isLoading = false,
  summary,
}: Props): JSX.Element {
  const { t } = useTranslation(['tables', 'forms', 'common']);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Navega a la actuación emparejada con la OT (entidad doble 1:1). Mismo
  // destino que ValidationsTable on-double-tap (index.tsx).
  const goToAction = useCallback(
    (o: { actionId?: string | null; orderId?: string | null; roid?: string | null }) => {
      if (!o.actionId) return;
      // Limpia la sesión de validación anterior (evita arrastrar origin/parentOrderId
      // rancios de un flujo previo) y graba el contexto propio con returnTo al informe.
      Object.values({
        ...formActionNaming,
        ...formActionValidationNaming,
        ...externalCompaniesNaming,
      }).forEach((key) => {
        if (key !== formActionNaming.NEW_ACTION_DATA) sessionStorage.removeItem(key);
      });
      sessionStorage.setItem(
        formActionNaming.NEW_VALIDATION_ACTION_DATA,
        compressSessionStorage(
          JSON.stringify({
            id: o.actionId,
            orderId: o.orderId,
            roid: o.roid,
            isActionEdited: 'false',
            returnTo: '/reporting/time-management',
          }),
        ),
      );
      // Desbloquear todas las tabs en la entrada, igual que el resto de flujos
      // (ShowOT, ValidationsTable, GeneralView). Robustez extra: ActionPage
      // también desbloquea al montar las pestañas (cubre reload).
      dispatch(actionTabsUnlockAll());
      navigate(`/work-orders/actions-validation/summary/${o.actionId}`);
    },
    [navigate, dispatch],
  );

  // Fila de turno RV (labor-entry-rv 2026-07-21): navega al detalle del
  // recorrido — el turno no tiene actuación emparejada (actionId null).
  const goToRoute = useCallback(
    (routeId: string) => {
      navigate(`/surveillance-route/${routeId}`);
    },
    [navigate],
  );

  // Feature flag canónico: en este tenant la columna "Tipo de hora"
  // existe solo si `showHoursType` está activo (mismo flag que el form
  // OperatorsAndVehicles consume). Cuando OFF, ocultamos la columna y
  // el chip; el total horas sigue visible.
  const showHoursType = useFeatureFlag('showHoursType', true);

  // ¿Hay externas en el dataset GLOBAL (no solo en la página)? Se usa el
  // summary global para que la columna "Operarios" sea estable entre páginas.
  const hasExternal =
    (summary?.externalCount ?? 0) > 0 ||
    rows.some((r) => r.kind === KIND_EXTERNAL);

  const toggleExpand = useCallback((id: string) => {
    setExpandedId((current) => (current === id ? null : id));
  }, []);

  // Columnas reales: Tipo | Operario/Empresa | Equipo | [Tipo hora] |
  // [Operarios] | Total | ▶. `colSpan` (usado por empty-state) excluye la
  // columna de expandir, igual que antes.
  // Nº TOTAL de columnas (Operario/Empresa, Equipo, [Tipo hora], [Operarios],
  // Total y la de expandir ▶). La columna "Tipo" se quitó por redundante con
  // la cabecera de sección. Las filas a todo el ancho (empty, detalle,
  // cabecera de sección) usan este colSpan COMPLETO para no dejar hueco a la
  // derecha.
  const colSpan = 4 + (showHoursType ? 1 : 0) + (hasExternal ? 1 : 0);
  // La etiqueta del subtotal abarca todo menos la columna Total y la de ▶.
  const subtotalLabelSpan = colSpan - 2;

  // Subtotal por sección, INLINE tras las filas de su naturaleza, usando el
  // `summary` GLOBAL. Se pinta SIEMPRE (sin condicionar a que la sección entera
  // quepa en la página) → el total nunca desaparece con paginación y queda
  // pegado a su sección (internas tras la última persona, externas tras la
  // última empresa) en vez de amontonado al final.

  return (
    <TableContainerStyled
      $isLoading={isLoading}
      data-testid="time-management-results"
    >
      <StyledTable>
        {/* Patrón ValidationsTable: <thead> HTML nativo (sin background
            heredado), <Th> canónicos con border-bottom negro. */}
        <thead>
          <tr>
            <Th>
              <TextHeader size="S">
                {t('forms:timeManagement.headers.subject')}
              </TextHeader>
            </Th>
            <Th>
              <TextHeader size="S">{t('tables:headers.team')}</TextHeader>
            </Th>
            {showHoursType && (
              <Th>
                <TextHeader size="S">
                  {t('tables:headers.hourType')}
                </TextHeader>
              </Th>
            )}
            {hasExternal && (
              <NumTh>
                <TextHeader size="S">
                  {t('forms:timeManagement.headers.operatorsNumber')}
                </TextHeader>
              </NumTh>
            )}
            <NumTh>
              <TextHeader size="S">{t('tables:headers.totalHours')}</TextHeader>
            </NumTh>
            <Th aria-label="expand" />
          </tr>
        </thead>
        <Tbody>
          {/* El empty inline solo si NO está cargando — durante el
              loading el spinner absolute cubre y el tbody queda vacío
              (no mostrar "No hay datos" mientras aún se busca). */}
          {rows.length === 0 && !isLoading && (
            <tr data-testid="time-management-empty">
              <EmptyCell colSpan={colSpan}>
                {t('forms:timeManagement.emptyState')}
              </EmptyCell>
            </tr>
          )}
          {rows.map((row, idx) => {
            const teamNames = (row.teamIDsSnapshot ?? [])
              .map((id) => teamNameById[id] ?? id)
              .filter(Boolean)
              .join(', ');
            const isExternal = row.kind === KIND_EXTERNAL;
            const prev = idx > 0 ? rows[idx - 1] : null;
            const prevExternal = prev ? prev.kind === KIND_EXTERNAL : null;
            // Las filas llegan ordenadas interno-primero (index.tsx) → una
            // cabecera de sección se emite al inicio y en cada cambio de
            // naturaleza.
            const showSectionHeader = idx === 0 || isExternal !== prevExternal;
            // Última fila de su naturaleza EN LA PÁGINA → debajo va el subtotal
            // de la sección (siempre, sin gate de paginación).
            const isLastInternalOnPage =
              !isExternal &&
              (idx === rows.length - 1 || rows[idx + 1].kind === KIND_EXTERNAL);
            const isLastExternalOnPage =
              isExternal && idx === rows.length - 1;
            const expanded = expandedId === row.subjectId;

            return (
              <React.Fragment key={row.subjectId}>
                {showSectionHeader && (
                  <SectionHeaderRow
                    data-testid={`section-${isExternal ? 'external' : 'internal'}`}
                  >
                    <SectionHeaderCell colSpan={colSpan}>
                      {isExternal ? (
                        <>
                          {`◆ ${t('forms:timeManagement.groups.external')} · `}
                          <SectionHint>
                            {t('forms:timeManagement.groups.externalFormula')}
                          </SectionHint>
                        </>
                      ) : (
                        `● ${t('forms:timeManagement.groups.internal')}`
                      )}
                    </SectionHeaderCell>
                  </SectionHeaderRow>
                )}
                <ClickableTr
                  onClick={() => toggleExpand(row.subjectId)}
                  aria-expanded={expanded}
                  data-testid={`operator-row-${row.subjectId}`}
                >
                  <Td data-testid={`operator-header-${row.subjectId}`}>
                    <Typography size="S" variant="semiBold">
                      {row.subjectName}
                    </Typography>
                  </Td>
                  <Td>
                    <Typography size="S">{teamNames || '—'}</Typography>
                  </Td>
                  {showHoursType && (
                    <Td>
                      {row.byHourType.length > 0 ? (
                        <ChipsRow>
                          {row.byHourType.map((b) => (
                            <Chip key={b.hourType}>
                              {b.hourType}: {formatHHMM(b.minutes)}
                            </Chip>
                          ))}
                        </ChipsRow>
                      ) : (
                        <Typography size="S">—</Typography>
                      )}
                    </Td>
                  )}
                  {hasExternal && (
                    <NumCell>
                      <Typography size="S">
                        {row.operatorsNumber != null
                          ? String(row.operatorsNumber)
                          : '—'}
                      </Typography>
                    </NumCell>
                  )}
                  <NumCell
                    data-testid={`operator-total-${row.subjectId}`}
                    title={
                      isExternal
                        ? t('forms:timeManagement.totalAllOperators')
                        : undefined
                    }
                  >
                    {isExternal ? (
                      <EstimatedText>
                        <Typography size="M" variant="semiBold">
                          {formatEstimatedHours(row.totalMinutes)}
                        </Typography>
                      </EstimatedText>
                    ) : (
                      <Typography size="M" variant="semiBold">
                        {formatHHMM(row.totalMinutes)}
                      </Typography>
                    )}
                  </NumCell>
                  <Td>
                    <ExpandIcon $expanded={expanded} aria-hidden>
                      ▶
                    </ExpandIcon>
                  </Td>
                </ClickableTr>

                {expanded && (
                  <SubRow data-testid={`operator-orders-${row.subjectId}`}>
                    <SubCell colSpan={colSpan}>
                      <SubTable>
                        <thead>
                          <tr>
                            <SubTh>{t('tables:headers.roid')}</SubTh>
                            <SubTh>{t('tables:headers.status')}</SubTh>
                            <SubTh>{t('common:simultaneous')}</SubTh>
                            <SubTh>
                              {t('forms:timeManagement.headers.predefined')}
                            </SubTh>
                            <SubTh>
                              {t('forms:timeManagement.headers.description')}
                            </SubTh>
                            <SubTh>{t('forms:workOrder.startDate')}</SubTh>
                            <SubTh>{t('forms:workOrder.endDate')}</SubTh>
                            {isExternal ? (
                              <>
                                <SubTh>
                                  {t('forms:timeManagement.headers.duration')}
                                </SubTh>
                                <SubTh>
                                  {t(
                                    'forms:timeManagement.headers.operatorsNumber',
                                  )}
                                </SubTh>
                                <SubTh>
                                  {t('forms:timeManagement.headers.subtotal')}
                                </SubTh>
                              </>
                            ) : (
                              <SubTh>{t('tables:headers.totalHours')}</SubTh>
                            )}
                            <SubTh aria-label="action" />
                          </tr>
                        </thead>
                        <tbody>
                          {row.orders.map((o) => (
                            <tr key={o.orderId}>
                              <SubTd>{o.roid}</SubTd>
                              <SubTd data-testid={`ot-state-${o.orderId}`}>
                                {o.state ? (
                                  <SpecialTag
                                    sizeTag="small"
                                    variant={o.state as variant}
                                  />
                                ) : (
                                  '—'
                                )}
                              </SubTd>
                              <SubTd data-testid={`ot-simultaneous-${o.orderId}`}>
                                {o.sourceType === 'surveillanceRoute' ? (
                                  // Distintivo de origen: la fila es el TURNO
                                  // de patrulla (ventana 1er→último tramo).
                                  <Chip $variant="external">
                                    {t('common:orderTypes.surveillanceRoute')}
                                  </Chip>
                                ) : o.isSimultaneous ? (
                                  <Chip $variant="external">
                                    {t('common:simultaneous')}
                                  </Chip>
                                ) : (
                                  '—'
                                )}
                              </SubTd>
                              <SubTd>{o.predefined || '—'}</SubTd>
                              <SubDescTd
                                title={o.description || undefined}
                                data-testid={`ot-desc-${o.orderId}`}
                              >
                                {o.description || '—'}
                                {/* Turno RV: mostrar de cuántos tramos se
                                    compone → se lee como un turno (basado en
                                    tramos), no como una OT. */}
                                {o.sourceType === 'surveillanceRoute' &&
                                  o.segmentsCount != null &&
                                  o.segmentsCount > 0 && (
                                    <>
                                      {' · '}
                                      {t('pages:surveillance.segmentsCountShort', {
                                        count: o.segmentsCount,
                                      })}
                                    </>
                                  )}
                                {/* Regla de la persona única: lo descontado por
                                    actuaciones declaradas de la persona. */}
                                {o.sourceType === 'surveillanceRoute' &&
                                  (o.deductedMinutes ?? 0) > 0 && (
                                    <>
                                      {' · '}
                                      <span
                                        data-testid={`ot-deducted-${o.orderId}`}
                                        title={t('pages:surveillance.shiftDeductedHint', {
                                          gross: formatHHMM(o.grossMinutes),
                                        })}
                                      >
                                        {t('pages:surveillance.shiftDeducted', {
                                          hours: formatHHMM(o.deductedMinutes),
                                        })}
                                      </span>
                                    </>
                                  )}
                              </SubDescTd>
                              <DateTimeSubTd data-testid={`ot-start-${o.orderId}`}>
                                {formatDateTime(o.startTime)}
                              </DateTimeSubTd>
                              <DateTimeSubTd data-testid={`ot-end-${o.orderId}`}>
                                {formatDateTime(o.endTime)}
                              </DateTimeSubTd>
                              {isExternal ? (
                                <>
                                  <SubTd>
                                    {o.durationMinutes != null
                                      ? formatHHMM(o.durationMinutes)
                                      : '—'}
                                  </SubTd>
                                  <SubTd>
                                    {o.operatorsNumber != null
                                      ? String(o.operatorsNumber)
                                      : '—'}
                                  </SubTd>
                                  <SubTd>{formatHHMM(o.minutes)}</SubTd>
                                </>
                              ) : (
                                <SubTd>{formatHHMM(o.minutes)}</SubTd>
                              )}
                              <ActionSubTd>
                                {o.actionId ? (
                                  <ButtonIconBackground
                                    variant="visibility"
                                    title={t(
                                      'forms:timeManagement.headers.goToAction',
                                    )}
                                    data-testid={`go-to-action-${o.orderId}`}
                                    onClick={() => goToAction(o)}
                                  />
                                ) : o.routeId ? (
                                  <ButtonIconBackground
                                    variant="visibility"
                                    title={t(
                                      'common:orderTypes.surveillanceRoute',
                                    )}
                                    data-testid={`go-to-route-${o.orderId}`}
                                    onClick={() => goToRoute(o.routeId!)}
                                  />
                                ) : null}
                              </ActionSubTd>
                            </tr>
                          ))}
                        </tbody>
                      </SubTable>
                    </SubCell>
                  </SubRow>
                )}

                {isLastInternalOnPage &&
                  summary &&
                  summary.internalCount > 0 && (
                    <SubtotalRow data-testid="subtotal-internal">
                      <SubtotalLabelCell colSpan={subtotalLabelSpan}>
                        <Typography size="S" variant="semiBold">
                          {t('forms:timeManagement.summary.internalReal')} ·{' '}
                          {summary.internalCount}
                        </Typography>
                      </SubtotalLabelCell>
                      <NumCell>
                        <Typography size="M" variant="semiBold">
                          {formatHHMM(summary.internalMinutes)}
                        </Typography>
                      </NumCell>
                      <Td aria-hidden />
                    </SubtotalRow>
                  )}
                {isLastExternalOnPage &&
                  summary &&
                  summary.externalCount > 0 && (
                    <SubtotalRow data-testid="subtotal-external">
                      <SubtotalLabelCell colSpan={subtotalLabelSpan}>
                        <Typography size="S" variant="semiBold">
                          {t('forms:timeManagement.summary.externalEstimated')} ·{' '}
                          {summary.externalCount}
                        </Typography>
                      </SubtotalLabelCell>
                      <NumCell>
                        <Typography size="M" variant="semiBold">
                          {formatEstimatedHours(summary.externalMinutes)}
                        </Typography>
                      </NumCell>
                      <Td aria-hidden />
                    </SubtotalRow>
                  )}
              </React.Fragment>
            );
          })}
        </Tbody>
      </StyledTable>
    </TableContainerStyled>
  );
}
